/**
 * Mirror OSINT Search platform-card inclusion
 * (`search-result-processor.service.ts` createEntries / buildPlatformResults).
 * Link Analysis should show the same sources as OSINT Search — not more, not less.
 */

function extractNestedValue(value: any): string {
  if (value == null) return '';
  if (typeof value === 'object' && !Array.isArray(value) && 'value' in value) {
    if (value.value == null) return '';
    return String(value.value);
  }
  return String(value);
}

function isMeaningfulAccount(account: any): boolean {
  if (!account || typeof account !== 'object' || Array.isArray(account)) {
    return false;
  }
  return Boolean(
    extractNestedValue(account.name) ||
      extractNestedValue(account.username) ||
      extractNestedValue(account.handle) ||
      extractNestedValue(account.uid) ||
      extractNestedValue(account.profile_url) ||
      extractNestedValue(account.url),
  );
}

function hasInlineAccountData(sourceData: any): boolean {
  if (!sourceData || typeof sourceData !== 'object') return false;
  return Boolean(
    extractNestedValue(sourceData.name) ||
      extractNestedValue(sourceData.username) ||
      extractNestedValue(sourceData.uid) ||
      (Array.isArray(sourceData.names) && sourceData.names.length > 0) ||
      (Array.isArray(sourceData.vpas) && sourceData.vpas.length > 0) ||
      extractNestedValue(sourceData.profile_url) ||
      extractNestedValue(sourceData.maps_url),
  );
}

function hasDisplayableFieldEntries(sourceData: any): boolean {
  const fields = sourceData?.fields;
  if (!fields || typeof fields !== 'object' || Array.isArray(fields)) {
    return false;
  }
  return Object.entries(fields).some(([key, rawField]) => {
    if (key === 'picture_url' || key === 'image_hash') return false;
    if (rawField == null) return false;
    if (typeof rawField !== 'object' || Array.isArray(rawField)) return true;
    if ('value' in rawField) {
      return rawField.value !== undefined && rawField.value !== null && rawField.value !== '';
    }
    return Object.keys(rawField).length > 0;
  });
}

/** True when OSINT Search would create a platform card for this source. */
export function osintSourceWouldDisplayInSearch(sourceData: any): boolean {
  if (!sourceData || typeof sourceData !== 'object' || Array.isArray(sourceData)) {
    return false;
  }

  const accounts = Array.isArray(sourceData.accounts) ? sourceData.accounts : [];
  if (accounts.some((account: unknown) => account && typeof account === 'object')) {
    return true;
  }
  if (isMeaningfulAccount(sourceData.account)) return true;
  if (hasInlineAccountData(sourceData)) return true;
  if (hasDisplayableFieldEntries(sourceData)) return true;
  if (sourceData.has_account === true || sourceData.has_account === false) return true;
  if (sourceData.category) return true;
  return false;
}

export function shouldDisplayOsintSourceOnGraph(node: any): boolean {
  if (!node) return false;
  const metadata = node.metadata || node.raw?.metadata || {};
  if (metadata.isIdentityHub || metadata.isSourceTypeHub) return true;

  const type = String(node.type || node.raw?.type || '').toLowerCase();
  const isPlatformNode =
    type === 'osint_source' ||
    Boolean(metadata.isOsintPlatformSource && metadata.sourceData);

  if (!isPlatformNode) return true;

  const sourceData = metadata.sourceData;
  if (!sourceData || typeof sourceData !== 'object') {
    return metadata.hasAccount !== false;
  }
  return osintSourceWouldDisplayInSearch(sourceData);
}
